import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';
import { StatusCodes } from 'http-status-codes';

import logger from 'logger';
import ApiService from 'services/ApiService/ApiService';
import TracingService from 'services/TracingService';
import { STEPPER_NO_STEP, STEPPER_OWNER_VERIFY } from 'enum/inputs';
import { ENUM_IDENTITY_STATUS } from 'enum/profile';
import makeAction from 'utils/makeAction';


import {
  IDENTITY_URL,
  IDENTITY_URL_GET,
  IMAGE_UPLOAD,
} from './Api';


const HANDLE_BACK_STEP = 'cam4/studio/HANDLE_BACK_STEP';
const HANDLE_NEXT_STEP = 'cam4/studio/HANDLE_NEXT_STEP';
const SAVE_ID_PICTURE = 'cam4/studio/SAVE_ID_PICTURE';
const SAVE_IDENTITY_ID = 'cam4/studio/SAVE_IDENTITY_ID';
const SET_ACTIVE_STEPPER = 'cam4/studio/SET_ACTIVE_STEPPER';
const UPDATE_ERROR_MESSAGE = 'cam4/studio/UPDATE_ERROR_MESSAGE';

const genericErrorMessage = 'Oops! Something went wrong, please try again !';
const genericErrorMessageImageUpload = 'Oops! Something went wrong with uploading your picture, please try again !';

const initialState = Immutable.fromJS({
  capture: null,
  files: [],
  identityId: null,
  stepperActiveStep: STEPPER_NO_STEP,
  errorMessage: null,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SAVE_ID_PICTURE:
      return state.set('capture', action.capture);

    case SAVE_IDENTITY_ID:
      return state.set('identityId', action.identityId);

    case HANDLE_BACK_STEP:
      return state.set('stepperActiveStep', action.stepperActiveStep - 1);

    case HANDLE_NEXT_STEP:
      return state.set('stepperActiveStep', action.stepperActiveStep + 1);

    case UPDATE_ERROR_MESSAGE:
      return state.set('errorMessage', action.errorMessage);

    case SET_ACTIVE_STEPPER:
      return state.set('stepperActiveStep', action.stepperActiveStep);

    default:
      return state;
  }
}

export const NAME = 'verification';

// input Selectors
const verification = (state) => state[NAME];

const capture = (state) => state[NAME].get('capture');
const files = (state) => state[NAME].get('files');
const identityId = (state) => state[NAME].get('identityId');
const errorMessage = (state) => state[NAME].get('errorMessage');
const stepperActiveStep = (state) => state[NAME].get('stepperActiveStep');

export const selector = createStructuredSelector({
  verification,
  capture,
  errorMessage,
  files,
  identityId,
  stepperActiveStep,
});

const setActiveStep = makeAction(SET_ACTIVE_STEPPER, 'stepperActiveStep');
const handleNext = makeAction(HANDLE_NEXT_STEP, 'stepperActiveStep');
const handleBack = makeAction(HANDLE_NEXT_STEP, 'stepperActiveStep');
const saveIdentity = makeAction(SAVE_IDENTITY_ID, 'identityId');
const saveIDPicture = makeAction(SAVE_ID_PICTURE, 'capture');
const updateErrorMessage = makeAction(UPDATE_ERROR_MESSAGE, 'errorMessage');

function saveIDPictureCall(username, image, identityType, imageType) {
  const data = {
    imageType,
    image,
  };
  const headers = {};
  const URL = ApiService.substitute(IMAGE_UPLOAD, {
    performerName: encodeURIComponent(username),
    identityType: encodeURIComponent(identityType),
  });

  return (dispatch) =>
    ApiService.post(URL, { data, headers }).then(
      () => {
        dispatch(updateErrorMessage(null));
      },
      () => dispatch(updateErrorMessage(genericErrorMessageImageUpload))
    );
}

function saveCameraPictureCall(user, image, identityType) {
  const data = {
    imageType: 'CAPTURE',
    image,
  };
  const headers = {};
  const URL = ApiService.substitute(IMAGE_UPLOAD, {
    performerName: encodeURIComponent(user.username),
    identityType: encodeURIComponent(identityType),
  });

  return (dispatch) =>
    ApiService.post(URL, { data, headers }).then(
      () => {
        dispatch(updateErrorMessage(null));

        return dispatch;
      },
      () => dispatch(updateErrorMessage(genericErrorMessageImageUpload))
    );
}

function getIdentity(user, fullName, birthdate, status) {
  const data = {
    studioName: user.username,
    name: fullName,
    accountType: user.username === user.studio.username ? 'STUDIO_OWNER' : 'STUDIO_ADMIN',
    birthdate,
  };

  const rejectedData = { birthdate };

  const URL = encodeURI(ApiService.substitute(IDENTITY_URL, { performerName: user.username }));

  const requestType = status === ENUM_IDENTITY_STATUS.REJECTED ? 'put' : 'post';

  const requestURL = status === ENUM_IDENTITY_STATUS.REJECTED ? `${URL}/MAIN` : URL;

  const dataToSend = status === ENUM_IDENTITY_STATUS.REJECTED ? rejectedData : data;

  return (dispatch) =>
    ApiService[requestType](requestURL, { data: dataToSend }).then(
      (res) => {
        if ((res.status === StatusCodes.OK && res.data.identityType) || res.status === StatusCodes.NO_CONTENT) {
          dispatch(updateErrorMessage(null));
          dispatch(saveIdentity(res.data.identityType));

          return res.data.identityType;
        }
        throw `getIdentity: ${res.status}`;

      },
      (error) => {
        logger.error(error);
        dispatch(updateErrorMessage(genericErrorMessage));
      }
    );
}

function fetchFlowProgress(user) {
  const headers = TracingService.generateHeaders();
  const URL = ApiService.substitute(IDENTITY_URL_GET, { username: user.username });

  return (dispatch) =>
    ApiService.get(URL, { headers })
      .then((res) => {
        const isValidRequest = res.status === StatusCodes.OK;
        // const identityId = (res.data[0] && res.data[0].identityType) || null;
        // const activeStep = isValidRequest && identityId
        //   ? STEPPER_OWNER_VERIFY.VERIFY_SNAPSHOT
        //   : STEPPER_OWNER_VERIFY.UPLOAD_ID;

        const userData = res.data;

        dispatch(setActiveStep(STEPPER_OWNER_VERIFY.UPLOAD_ID));
        dispatch(saveIdentity(userData));

        if (!isValidRequest) {
          throw res.status;
        }
      })
      .catch((error) => {
        dispatch(updateErrorMessage(genericErrorMessage));
        dispatch(setActiveStep(STEPPER_OWNER_VERIFY.UPLOAD_ID));
        logger.error({ msg: 'fetchFlowProgress:', error });
      });
}

export const actionCreators = {
  updateErrorMessage,
  saveCameraPictureCall,
  saveIDPictureCall,
  saveIDPicture,
  getIdentity,
  handleNext,
  handleBack,
  // deprecated
  fetchFlowProgress,
};
